import { useMemo } from 'react';

import { appRoutes, type AppRouteKey } from '@/app/routes';
import type { BreadcrumbItem } from '@/components/layout/AppBreadcrumb';

export interface UseBreadcrumbsParams {
  routeKey: AppRouteKey;
  selectedClientId: string | undefined;
  selectedProjectId: string | undefined;
  clientLabel: string;
  assessmentLabel: string;
  selectedProjectName: string | null;
}

/**
 * Computes the breadcrumb trail for the current route. Returns a stable array reference
 * via useMemo — only recomputes when route or label values change.
 */
export function useBreadcrumbs({
  routeKey,
  selectedClientId,
  selectedProjectId,
  clientLabel,
  assessmentLabel,
  selectedProjectName,
}: UseBreadcrumbsParams): BreadcrumbItem[] {
  return useMemo((): BreadcrumbItem[] => {
    const portfolio: BreadcrumbItem = { title: 'Portfolio', href: appRoutes.portfolio };

    if (!selectedClientId) {
      switch (routeKey) {
        case 'portfolio':
          return [{ title: 'Portfolio' }];
        case 'skills':
          return [portfolio, { title: 'Skills' }];
        case 'skill-edit':
          return [portfolio, { title: 'Skills', href: appRoutes.skills }, { title: 'Edit Skill' }];
        case 'skill-template':
          return [
            portfolio,
            { title: 'Skills', href: appRoutes.skills },
            { title: 'Skill Template' },
          ];
        case 'settings-profile':
          return [portfolio, { title: 'Settings' }, { title: 'Profile' }];
        default:
          return [portfolio];
      }
    }

    const clientItem: BreadcrumbItem = {
      title: clientLabel,
      href: appRoutes.projects(selectedClientId),
    };

    if (!selectedProjectId) {
      return [portfolio, { title: clientLabel }];
    }

    const projectDisplayName = selectedProjectName ?? assessmentLabel;
    const projectItem: BreadcrumbItem = {
      title: projectDisplayName,
      href: appRoutes.projectHome(selectedClientId, selectedProjectId),
    };
    const assessmentsItem: BreadcrumbItem = {
      title: 'Assessment',
      href: appRoutes.projectAssessments(selectedProjectId, selectedClientId),
    };
    const assetLibraryItem: BreadcrumbItem = {
      title: 'Asset Library',
      href: appRoutes.projectAssetLibrary(selectedProjectId, selectedClientId),
    };

    switch (routeKey) {
      case 'projects':
        return [portfolio, clientItem];
      case 'project-detail':
        return [portfolio, clientItem, { title: projectDisplayName }];
      case 'project-weekly-updates':
      case 'project-weekly-update-create':
      case 'project-weekly-update-edit':
      case 'project-weekly-update-view':
        return [portfolio, clientItem, projectItem, { title: 'Weekly Status Update' }];
      case 'project-assessments':
        return [portfolio, clientItem, projectItem, { title: 'Assessments' }];
      case 'asset-library':
        return selectedProjectId
          ? [portfolio, clientItem, projectItem, { title: 'Asset Library' }]
          : [portfolio, clientItem, { title: 'Asset Library' }];
      case 'asset-library-note':
        return [portfolio, clientItem, projectItem, assetLibraryItem, { title: 'Note' }];
      case 'file-cabinet':
        return [portfolio, clientItem, projectItem, { title: 'File Cabinet' }];
      case 'workspace':
        return [portfolio, clientItem, projectItem, { title: 'Workspace' }];
      case 'step-1':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 01 · Interview Prep' },
        ];
      case 'step-1-generating':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 01 · Generating' },
        ];
      case 'step-1-output':
        return [portfolio, clientItem, projectItem, assessmentsItem, { title: 'Step 01 · Output' }];
      case 'step-1-review':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 01 · Interview Guide' },
        ];
      case 'step-2':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 02 · Transcript Analysis' },
        ];
      case 'step-2-generating':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 02 · Generating' },
        ];
      case 'step-2-output':
        return [portfolio, clientItem, projectItem, assessmentsItem, { title: 'Step 02 · Output' }];
      case 'step-3':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 03 · Use-Case Definition' },
        ];
      case 'step-3-generating':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 03 · Generating' },
        ];
      case 'step-3-output':
        return [portfolio, clientItem, projectItem, assessmentsItem, { title: 'Step 03 · Output' }];
      case 'step-4':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 04 · Priority Scoring' },
        ];
      case 'step-4-generating':
        return [
          portfolio,
          clientItem,
          projectItem,
          assessmentsItem,
          { title: 'Step 04 · Generating' },
        ];
      case 'step-4-output':
        return [portfolio, clientItem, projectItem, assessmentsItem, { title: 'Step 04 · Output' }];
      default:
        return [portfolio];
    }
  }, [
    routeKey,
    selectedClientId,
    selectedProjectId,
    clientLabel,
    assessmentLabel,
    selectedProjectName,
  ]);
}
